// Recolour slots — the colour counterpart to sockets. A {@link Part} may tag
// itself with a `slot` (e.g. "team"); its own `color` is that slot's default.
// A consumer then overrides every part in a slot with one colour at build time,
// so the same model wears any team colour without being re-authored. The model
// publishes the slot (and its default) just by tagging parts; the consumer fills
// it — exactly as a socket publishes an anchor the consumer fills with content.
//
// `applySlots` is the override (used by `instantiateModel` and the preview);
// `slotsOf` enumerates a model's slots and their defaults (used by a viewer
// colour picker to know which swatches to show and where to start).

import type { ColorRepresentation, Model, Part } from "./types";

/** Return `parts` with every slotted part recoloured by `slots[part.slot]`.
 *  Parts whose slot is absent from `slots` (or untagged parts) pass through
 *  unchanged; the input array is never mutated. Returns the input as-is when no
 *  override applies, so the common (no-slots) path allocates nothing. */
export function applySlots(parts: Part[], slots?: Record<string, ColorRepresentation>): Part[] {
  if (!slots) return parts;
  let changed = false;
  const out = parts.map((p) => {
    if (p.slot !== undefined && slots[p.slot] !== undefined) {
      changed = true;
      return { ...p, color: slots[p.slot]! };
    }
    return p;
  });
  return changed ? out : parts;
}

/** One recolour slot a model publishes: its id and the default colour (the
 *  colour of the first part tagged with that slot). */
export interface ModelSlot {
  id: string;
  default: ColorRepresentation;
}

/** Every recolour slot defined across a model's variants, each with its default
 *  colour, in first-seen order. Empty when the model tags no parts with a slot
 *  (the usual case) — a viewer shows a colour picker only when this is non-empty. */
export function slotsOf(model: Model): ModelSlot[] {
  const seen = new Map<string, ColorRepresentation>();
  for (const v of model.variants) {
    for (const p of v.parts()) {
      if (p.slot !== undefined && !seen.has(p.slot)) seen.set(p.slot, p.color);
    }
  }
  return [...seen].map(([id, def]) => ({ id, default: def }));
}
